import React from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  InputGroup,
  InputRightElement,
  Box,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon } from '@chakra-ui/icons';

export default function StyledDropdown({
  options = [],
  value,
  onChange,
  placeholder = 'Select',
  bg,
  color,
  borderColor,
  isDisabled = false,
  width = '100%',
  isClearable = true,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  const hasValue = Boolean(value);
  const menuBg = useColorModeValue('white', 'gray.800');
  const menuBorder = useColorModeValue('gray.200', 'gray.700');

  const itemActiveBg = useColorModeValue('blue.50', 'blue.900');
  const itemHoverBg = useColorModeValue('blue.100', 'blue.800');
  const itemText = useColorModeValue('gray.800', 'gray.100');

  return (
    <Menu
      isLazy
      strategy="fixed"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <MenuButton w={width} isDisabled={isDisabled}>
        <InputGroup>
          <Input
            readOnly
            value={selectedLabel}
            placeholder={placeholder}
            bg={bg}
            color={color}
            border="1px solid"
            borderColor={borderColor}
            fontWeight="500"
            borderRadius="lg"
            cursor="pointer"
            pr="3rem"
            _hover={{ borderColor: 'blue.400' }}
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 2px rgba(49,130,206,0.3)',
            }}
          />

          <InputRightElement w="3rem" pointerEvents="auto">
            <Box display="flex" alignItems="center" gap={2}>
              {isClearable && hasValue && (
                <Box
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange?.(null);
                  }}
                >
                  <CloseIcon boxSize="10px" color="gray.500" />
                </Box>
              )}

              <Box
                transition="transform 0.25s ease"
                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
              >
                <ChevronDownIcon color="gray.500" />
              </Box>
            </Box>
          </InputRightElement>
        </InputGroup>
      </MenuButton>

      <MenuList
        maxH="260px"
        overflowY="auto"
        zIndex="popover"
        borderRadius="lg"
        boxShadow="xl"
        p={1}
        bg={menuBg}
        borderColor={menuBorder}
      >
        {options.map((opt) => {
          const isActive = opt.value === value;

          return (
            <MenuItem
              key={opt.value}
              bg={isActive ? itemActiveBg : 'transparent'}
              color={itemText}
              fontWeight={isActive ? 'semibold' : 'normal'}
              borderRadius="md"
              _hover={{
                bg: itemHoverBg,
                color: itemText,
              }}
              _focus={{
                bg: itemHoverBg,
              }}
              onClick={() => onChange?.(opt.value)}
            >
              {opt.label}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}
